/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable security/detect-object-injection */
/* eslint-disable functional/no-let */
/* eslint-disable no-console */
/* eslint-disable functional/immutable-data */
"use client";

// import { formatInTimeZone } from "date-fns-tz";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { type JSX, useEffect, useState } from "react";

import { supabase } from "@/lib/supabase-client";
import {
  type AgendaItem,
  type MeetingAgendaAttributes,
  type MeetingAgendaContent,
} from "@/types/meeting-agenda-page";
import { generateCalendarFile } from "@/utils/calendar-ics-generator";
import { drawCityscapeIllustration } from "@/utils/design-elements";
import { getSharePageUrl } from "@/utils/share-page-url";

// Define the timezone we want to use
// const NEW_YORK_TIMEZONE = "America/New_York";

interface RealTimeAnnualMeetingProps {
  readonly initialData: MeetingAgendaContent;
}

interface SupabasePayload {
  readonly [key: string]: unknown;
  readonly headline?: string;
  readonly lead_paragraph?: string;
  readonly body?: string;
  readonly hero_image?: {
    readonly url: string;
    readonly width: number;
    readonly height: number;
    readonly alternativeText: string;
  };
  readonly seo?: {
    readonly description: string;
  };
  readonly agenda?: {
    readonly data: {
      readonly id: number;
      readonly attributes: {
        readonly Year: string;
        readonly Description: readonly unknown[];
        readonly VenueLink: string;
        readonly createdAt: string;
        readonly updatedAt: string;
        readonly publishedAt: string;
        readonly agenda_items?: {
          readonly data: ReadonlyArray<{
            readonly id: number;
            readonly attributes: {
              readonly NameType?: string;
              readonly Start?: string;
              readonly End?: string;
              readonly Description?: readonly unknown[];
              readonly Name: string;
              readonly IsSession?: boolean;
              readonly IsLightningSession?: boolean;
              readonly Moderator?: string;
              readonly IsKeynote?: boolean;
              readonly createdAt: string;
              readonly updatedAt: string;
              readonly publishedAt: string;
            };
          }>;
        };
      };
    };
  };
}

const SharePage = dynamic(() => import("@/components/social/share-page"), { ssr: false });

const mapPayloadToContentAttributes = (
  payload: SupabasePayload,
): Partial<MeetingAgendaAttributes> => {
  const mappedData: Partial<MeetingAgendaAttributes> = {};

  if (payload.headline) mappedData.Headline = payload.headline;
  if (payload.lead_paragraph) mappedData.LeadParagraph = payload.lead_paragraph;
  if (payload.body) mappedData.Body = payload.body;
  // Type assertion: SupabasePayload.agenda has deeply readonly types from ESLint
  // functional/prefer-readonly-type, but MeetingAgendaAttributes.agenda expects mutable types.
  // Safe because this mapping function only reads the data, never mutates it.
  if (payload.agenda)
    mappedData.agenda = payload.agenda as unknown as MeetingAgendaAttributes["agenda"];

  return mappedData;
};

const updateContentFromPayload = (
  prevContent: MeetingAgendaContent,
  newData: SupabasePayload,
): MeetingAgendaContent => {
  const mappedData = mapPayloadToContentAttributes(newData);

  return {
    ...prevContent,
    data: {
      ...prevContent.data,
      attributes: {
        ...prevContent.data.attributes,
        ...mappedData,
      },
    },
  };
};

/**
 * Formats a time range from ISO strings, ignoring timezone information
 * Directly extracts hours and minutes from the string
 * Handles cases where only start time is available
 */

const formatTimeRange = (
  startDateTime: string | undefined,
  endDateTime: string | undefined,
): {
  readonly startTime: string;
  readonly endTime: string;
  readonly startDatetime: string;
  readonly endDatetime: string;
} => {
  const defaultResult = {
    endDatetime: "",
    endTime: "",
    startDatetime: "",
    startTime: "",
  };

  // If no start time, return empty result
  if (!startDateTime) return defaultResult;

  try {
    // Extract hours and minutes directly from the ISO strings
    // Format: "2025-03-19T17:30:00.000Z"
    const parts = startDateTime.split("T");
    if (parts.length < 2 || !parts[1]) return defaultResult;
    const startTimeParts = parts[1].split(":");
    if (!startTimeParts[0] || !startTimeParts[1]) return defaultResult;

    // Get hours as numbers (first part of the time string)
    const startHours = parseInt(startTimeParts[0], 10);

    // Get minutes (second part of the time string)
    const startMinutes = parseInt(startTimeParts[1], 10);

    // Determine period
    const startIsPM = startHours >= 12;
    const startPeriod = startIsPM ? "PM" : "AM";

    // Convert to 12-hour format
    const to12Hour = (hours: number): number => {
      if (hours > 12) return hours - 12;
      if (hours === 0) return 12;
      return hours;
    };

    const startHour12 = to12Hour(startHours);

    // If there's no end time, format start time with period
    if (!endDateTime) {
      const formattedStartTime = `${startHour12}${startMinutes > 0 ? `:${startMinutes.toString().padStart(2, "0")}` : ""} ${startPeriod}`;

      return {
        endDatetime: "",
        endTime: "",
        startDatetime: startDateTime,
        startTime: formattedStartTime,
      };
    }

    // If we have both start and end times, continue with existing logic
    const endParts = endDateTime?.split("T");
    if (!endParts?.[1]) return defaultResult;
    const endTimeParts = endParts[1].split(":");
    if (!endTimeParts[0] || !endTimeParts[1]) return defaultResult;
    const endHours = parseInt(endTimeParts[0], 10);
    const endMinutes = parseInt(endTimeParts[1], 10);

    // Check if both times are in the same period
    const endIsPM = endHours >= 12;
    const samePeriod = startIsPM === endIsPM;

    // Format start time (without period if same period as end time)
    const startTime = `${startHour12}${startMinutes > 0 ? `:${startMinutes.toString().padStart(2, "0")}` : ""}`;

    // Format end time with period
    const endHour12 = to12Hour(endHours);
    const endTime = `${endHour12}${endMinutes > 0 ? `:${endMinutes.toString().padStart(2, "0")}` : ""} ${endIsPM ? "PM" : "AM"}`;

    return {
      endDatetime: endDateTime,
      endTime,
      startDatetime: startDateTime,
      startTime: samePeriod ? startTime : `${startTime} ${startPeriod}`,
    };
  } catch (error) {
    console.error("Error formatting time range:", error);
    return defaultResult;
  }
};

/**
 * Formats a date to display as a nice header (e.g., "Sunday, March 22nd")
 * @param dateString - ISO date string
 * @returns Formatted date string with day of week, month, and day with ordinal suffix
 */
const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);

  // Add ordinal suffix to the day
  const day = date.getDate();
  let suffix = "th";
  if (day % 10 === 1 && day !== 11) suffix = "st";
  else if (day % 10 === 2 && day !== 12) suffix = "nd";
  else if (day % 10 === 3 && day !== 13) suffix = "rd";

  return (
    date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      weekday: "long",
    }) + suffix
  );
};

/**
 * Groups agenda items by day
 * @param items - Array of agenda items
 * @returns Object with ISO date strings as keys and arrays of agenda items as values
 */
const groupItemsByDay = (items: readonly AgendaItem[]): Record<string, readonly AgendaItem[]> => {
  return items.reduce<Record<string, AgendaItem[]>>((groups, item) => {
    if (!item.attributes.Start) return groups;

    // Extract just the date part (YYYY-MM-DD)
    const dateString = new Date(item.attributes.Start).toISOString().split("T")[0];

    if (dateString) {
      if (!groups[dateString]) {
        groups[dateString] = [];
      }
      groups[dateString].push(item);
    }
    return groups;
  }, {});
};

/**
 * Formats rich text content from Strapi into React elements
 * @param content - Rich text content from Strapi
 * @returns Formatted React elements
 */
interface TextNode {
  readonly text?: string;
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly underline?: boolean;
}

interface ContentBlock {
  readonly type: string;
  readonly format?: string;
  readonly children: ReadonlyArray<TextNode | { readonly children: readonly TextNode[] }>;
}

const formatRichTextDescription = (content: readonly ContentBlock[]): React.ReactNode => {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return null;
  }

  return (
    <>
      {content.map((block, blockIndex) => {
        // Handle paragraph blocks
        if (block.type === "paragraph") {
          return (
            <p key={`p-${blockIndex}`}>
              {block.children.map((textNode: unknown, textIndex: number) => {
                const node = textNode as TextNode;
                const text = node.text || "";

                // Apply formatting based on text node properties
                if (node.bold) {
                  return <strong key={`text-${blockIndex}-${textIndex}`}>{text}</strong>;
                } else if ((textNode as TextNode).italic) {
                  return <em key={`text-${blockIndex}-${textIndex}`}>{text}</em>;
                } else if ((textNode as TextNode).underline) {
                  return <u key={`text-${blockIndex}-${textIndex}`}>{text}</u>;
                } else {
                  return (
                    <React.Fragment key={`text-${blockIndex}-${textIndex}`}>{text}</React.Fragment>
                  );
                }
              })}
            </p>
          );
        }

        // Handle list blocks
        else if (block.type === "list") {
          const ListComponent = block.format === "ordered" ? "ol" : "ul";

          return (
            <ListComponent key={`list-${blockIndex}`}>
              {(block.children as Array<{ children: TextNode[] }>).map(
                (listItem, listItemIndex) => (
                  <li key={`li-${blockIndex}-${listItemIndex}`}>
                    {listItem.children.map((textNode: TextNode, textIndex: number) => {
                      const text = textNode.text || "";

                      // Apply formatting within list items
                      if (textNode.bold) {
                        return (
                          <strong key={`li-text-${blockIndex}-${listItemIndex}-${textIndex}`}>
                            {text}
                          </strong>
                        );
                      } else if (textNode.italic) {
                        return (
                          <em key={`li-text-${blockIndex}-${listItemIndex}-${textIndex}`}>
                            {text}
                          </em>
                        );
                      } else if (textNode.underline) {
                        return (
                          <u key={`li-text-${blockIndex}-${listItemIndex}-${textIndex}`}>{text}</u>
                        );
                      } else {
                        return (
                          <React.Fragment
                            key={`li-text-${blockIndex}-${listItemIndex}-${textIndex}`}
                          >
                            {text}
                          </React.Fragment>
                        );
                      }
                    })}
                  </li>
                ),
              )}
            </ListComponent>
          );
        }

        // Return null for unhandled block types
        return null;
      })}
    </>
  );
};

const displayAgendaItems = (agendaItems: readonly AgendaItem[] | undefined): JSX.Element | null => {
  if (!agendaItems || agendaItems.length === 0) {
    console.log("No agenda items to display");
    return null;
  }

  console.log("Agenda items from displayAgendaItems:", agendaItems);

  // Group items by day
  const itemsByDay = groupItemsByDay(agendaItems);

  // Sort days chronologically
  const sortedDays = Object.keys(itemsByDay).sort();

  return (
    <div className="agenda-days-container">
      {sortedDays.map((day) => (
        <section className="meeting-agenda" key={day}>
          <header>
            <h2>{formatDateHeader(day)}</h2>
          </header>
          <dl>
            {itemsByDay[day]?.map((item) => {
              const { startTime, endTime, startDatetime, endDatetime } = formatTimeRange(
                item.attributes.Start,
                item.attributes.End,
              );

              return (
                <React.Fragment key={item.id}>
                  <dt>
                    {startTime && (
                      <>
                        <time dateTime={startDatetime}>{startTime}</time>
                        {endTime && (
                          <>
                            –<time dateTime={endDatetime}>{endTime}</time>
                          </>
                        )}
                      </>
                    )}
                  </dt>
                  <dd
                    {...(item.attributes.IsSession ? { "data-is-session": "true" } : {})}
                    {...(item.attributes.IsKeynote ? { "data-is-keynote": "true" } : {})}
                  >
                    <strong className="agenda-item-name">
                      {item.attributes.IsKeynote && (
                        <svg
                          aria-hidden="true"
                          height="24"
                          viewBox="0 0 24 24"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M23.7 7.1H6.8c0-.5.2-1 .5-1.5s.7-.8 1.2-.9c.2.2.5.3.7.4.2 0 .5.1.8.1.7 0 1.3-.3 1.8-.8s.8-1.1.8-1.8-.3-1.3-.8-1.8S10.7 0 10 0 8.7.2 8.2.7c-.5.4-.8 1-.8 1.7-1 .4-1.8 1-2.4 1.9-.6.9-.9 1.8-.9 2.9H.3l1.3 9.1H6l.7 7.8h10.4l.7-7.8h4.4l1.3-9.1zm-8.8 14.3H9.1l-.7-7.8h7.3l-.7 7.8zm5.2-7.8h-2l.2-2.6H5.6l.2 2.6h-2l-.6-3.9h17.4l-.6 3.9z" />
                        </svg>
                      )}
                      {item.attributes.IsSession && (
                        <svg
                          aria-hidden="true"
                          height="24"
                          viewBox="0 0 24 24"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14.4 7.2c0 .7-.2 1.2-.7 1.7-.5.5-1 .7-1.7.7s-1.2-.2-1.7-.7c-.5-.5-.7-1-.7-1.7s.2-1.2.7-1.7 1-.7 1.7-.7 1.2.2 1.7.7.7 1 .7 1.7zM7.2 12c-.7 0-1.2.2-1.7.7-.5.5-.7 1-.7 1.7s.2 1.2.7 1.7c.5.5 1 .7 1.7.7s1.2-.2 1.7-.7.7-1 .7-1.7-.2-1.2-.7-1.7-1-.7-1.7-.7zm9.6 0c-.7 0-1.2.2-1.7.7s-.7 1-.7 1.7.2 1.2.7 1.7 1 .7 1.7.7 1.2-.2 1.7-.7c.5-.5.7-1 .7-1.7s-.2-1.2-.7-1.7c-.5-.5-1-.7-1.7-.7zm7.2 0c0 1.7-.3 3.2-.9 4.7s-1.5 2.7-2.6 3.8-2.3 1.9-3.8 2.6-3 .9-4.7.9-3.2-.3-4.7-.9-2.7-1.5-3.8-2.6-1.9-2.3-2.6-3.8-.9-3-.9-4.7.3-3.2.9-4.7 1.5-2.7 2.6-3.8S5.8 1.6 7.3.9s3-.9 4.7-.9 3.2.3 4.7.9 2.7 1.5 3.8 2.6 1.9 2.3 2.6 3.8.9 3 .9 4.7zm-2.4 0c0-2.7-.9-5-2.8-6.8S14.7 2.4 12 2.4s-5 .9-6.8 2.8S2.4 9.3 2.4 12s.9 5 2.8 6.8 4.1 2.8 6.8 2.8 5-.9 6.8-2.8 2.8-4.1 2.8-6.8z" />
                        </svg>
                      )}
                      {item.attributes.IsLightningSession && (
                        <svg
                          aria-hidden="true"
                          className="lightning-icon"
                          height="24"
                          viewBox="0 0 24 24"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M8.4 24v-9.6H4.8V0h12l-2.4 8.4h4.8L8.4 24z" />
                        </svg>
                      )}
                      {item.attributes.Name}
                    </strong>
                    {item.attributes.Moderator && (
                      <span className="moderator">
                        Moderator(s): <em>{item.attributes.Moderator}</em>
                      </span>
                    )}
                    {item.attributes.Description && item.attributes.Description.length > 0 && (
                      <>
                        {formatRichTextDescription(item.attributes.Description as ContentBlock[])}
                      </>
                    )}
                  </dd>
                </React.Fragment>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
};

const RealTimeMeetingAgendaClient = ({
  initialData,
}: RealTimeAnnualMeetingProps): React.JSX.Element => {
  const [content, setContent] = useState<MeetingAgendaContent>(initialData);

  useEffect(() => {
    // Log initial data to console
    console.log("Initial content:", initialData);

    // Log agenda if it exists
    if (initialData?.data?.attributes?.agenda) {
      console.log("Agenda data:", initialData.data.attributes.agenda);

      // Log agenda items if they exist
      const agendaItems = initialData.data.attributes.agenda.data?.attributes?.agenda_items?.data;
      if (agendaItems) {
        console.log("Agenda items:", agendaItems);
      } else {
        console.log("No agenda items found in the initial data");
      }
    } else {
      console.log("No agenda found in the initial data");
    }

    const channel = supabase
      .channel("meeting_agendas")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "meeting_agendas",
        },
        (payload: { eventType: string; new: SupabasePayload }) => {
          console.log("Received payload:", payload);

          // Log agenda from real-time update if it exists
          if (payload.eventType === "UPDATE" && payload.new) {
            if (payload.new.agenda) {
              console.log("Updated agenda data:", payload.new.agenda);

              // Log agenda items from the update if they exist
              const updatedAgendaItems = payload.new.agenda.data?.attributes?.agenda_items?.data;
              if (updatedAgendaItems) {
                console.log("Updated agenda items:", updatedAgendaItems);
              } else {
                console.log("No agenda items found in the update");
              }
            }

            setContent((prevContent) => updateContentFromPayload(prevContent, payload.new));
          }
        },
      )
      .subscribe();

    return (): void => {
      supabase
        .removeChannel(channel)
        .catch((err) => console.error("Failed to remove channel", err));
    };
  }, [initialData]);

  const { Headline, LeadParagraph, Body, HeroImage, agenda } = content.data.attributes;

  // Log the complete content data structure with indentation for better readability
  console.log("Current content attributes:", JSON.stringify(content.data.attributes, null, 2));

  return (
    <div className="application-shell-cell application-shell-cell-body align-left">
      <main className="single-type">
        <div className="single-type-cell single-type-cell-content">
          <div className="level-1-heading-container-with-sharing-options">
            <div className="cell">
              <h1>{Headline}</h1>
            </div>
            <div className="cell sharing-options">
              <div className="rss-feed-with-share-page">
                <div className="rss-feed-with-share-page-cell">
                  <SharePage text={Headline} url={getSharePageUrl()} />
                </div>
              </div>
            </div>
          </div>
          <p className="lead-paragraph">{LeadParagraph}</p>
          <ul className="button-list" style={{ display: "none" }}>
            <li>
              <button
                className="button-with-icon"
                onClick={() =>
                  generateCalendarFile({
                    endDate: "2025-03-22",
                    endTime: "11:00:00",
                    eventDescription:
                      "Join experts exploring how AI is transforming PI diagnosis and management.",
                    eventName:
                      "Second Annual Artificial Intelligence (AI) and Primary Immunodeficiency (PI) Conference",
                    location:
                      "New York Marriott Downtown, 85 West Street at Albany St, New York, NY 10006, United States",

                    options: {
                      domainName: process.env.NEXT_PUBLIC_DOMAIN_NAME,
                      filename: "aiml4pi-annual-meeting-calendar-event.ics",
                      prodId:
                        "-//Artificial Intelligence (AI) and Primary Immunodeficiency (PI)//Event Reminder//EN",
                      utm_campaign: "aiml4pi_annual_meeting",
                      utm_content: "annual_meeting_calendar_download",
                    },
                    startDate: "2025-03-19",
                    startTime: "17:30:00",
                  })
                }
              >
                <svg height="22" viewBox="0 0 20 22" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 20v-2h4V8H4v4H2V2h3V0h2v2h8V0h2v2h3v18h-6zm-7 2l-1.4-1.4L8.2 18H0v-2h8.2l-2.6-2.6L7 12l5 5-5 5zM4 6h14V4H4v2zm0 0V4v2z" />
                </svg>
                Save to Calendar
              </button>
            </li>
            <li>
              <a
                className="button-with-icon"
                href="https://www.google.com/maps/dir//85+West+Street+at,+Albany+St,+New+York,+NY+10006,+United+States/@40.7094913,-74.0968871,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x89c25a1090488a95:0xb7522984917c278f!2m2!1d-74.0144862!2d40.7095206?entry=ttu&g_ep=EgoyMDI1MDMxMi4wIKXMDSoASAFQAw%3D%3D"
                rel="noreferrer"
                target="_blank"
              >
                <svg height="22" viewBox="0 0 20 22" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.3 13.8h1.9V11h3.2v2.3l3.2-3.2-3.2-3.2v2.3H6.3v4.6zM10 21L0 11 10 1l10 10-10 10zm0-2.6l7.4-7.4L10 3.6 2.6 11l7.4 7.4z" />
                </svg>
                Get Directions
              </a>
            </li>
          </ul>
          <hr />
          {Body && <div dangerouslySetInnerHTML={{ __html: Body }} />}

          <div className="agenda-container" style={{ display: "none" }}>
            {agenda?.data?.attributes?.agenda_items?.data ? (
              displayAgendaItems(agenda.data.attributes.agenda_items.data)
            ) : (
              <p>Agenda details will be available soon.</p>
            )}
          </div>

          <hr />
          <button
            className="text-link-with-illustration hero-illustration cityscape"
            onClick={() =>
              generateCalendarFile({
                endDate: "2025-03-22",
                endTime: "11:00:00",
                eventDescription:
                  "Join experts exploring how AI is transforming PI diagnosis and management.",
                eventName:
                  "Second Annual Artificial Intelligence (AI) and Primary Immunodeficiency (PI) Conference",
                location:
                  "New York Marriott Downtown, 85 West Street at Albany St, New York, NY 10006, United States",

                options: {
                  domainName: process.env.NEXT_PUBLIC_DOMAIN_NAME,
                  filename: "aiml4pi-annual-meeting-calendar-event.ics",
                  prodId:
                    "-//Artificial Intelligence (AI) and Primary Immunodeficiency (PI)//Event Reminder//EN",
                  utm_campaign: "aiml4pi_annual_meeting",
                  utm_content: "annual_meeting_calendar_download",
                },
                startDate: "2025-03-19",
                startTime: "17:30:00",
              })
            }
          >
            <span className="label" style={{ display: "none" }}>
              Save Agenda to Your Calendar
            </span>
            {drawCityscapeIllustration({
              height: 1080,
              width: 1902,
            })}
          </button>
        </div>
        <div className="single-type-cell single-type-cell-hero-media">
          <div className="hero-media-container">
            <Image
              alt={HeroImage.data.attributes.alternativeText || ""}
              className="carousel-item-media"
              data-shape="chip"
              height={HeroImage.data.attributes.height}
              priority={true}
              src={HeroImage.data.attributes.url}
              width={HeroImage.data.attributes.width}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default RealTimeMeetingAgendaClient;
